// DT-3a — **THE STANDING DELIVERY AUDIT: THE MACHINE STOPS GRADING ITSELF.**
//
// HER ORDER (DT-3, verbatim): *"After every morning: sends vs received-beacons vs displays, per
// member, the gap list published to the dashboard. The morning job's own report is BANNED from the
// word 'delivered' for any send with no beacon, and the ship-claims law extends: any future claim
// that she or any member RECEIVED something must cite beacon rows, never Apple's 201."*
//
// **MEASURED BEFORE A LINE OF THIS WAS WRITTEN (R25), AND THE HEADLINE IS LITERAL.** The morning's
// own report is `{ scanned, sent, skipped, devices }` and `sent` counts `result.ok` — Apple's 201 and
// nothing else. It is written onto the ledger as `status = 'sent'`, `morningHealth` counts it, and
// `morningStrip` prints it on her Overview under the word **Sent**. So the morning grades itself
// entirely on the push service's acknowledgement, which is the exact thing this whole standard opens
// by refusing: *"Apple's 201 means Apple took the bytes. It has never meant a phone rang."*
//
// **AND THE TWO TABLES THAT KNOW BETTER HAVE NEVER BEEN INTRODUCED.** DT-1c built the beacon strip
// and DT-4a built the per-member chain, and both read `push_beacons` over a ROLLING WINDOW. Nothing
// anywhere joins a MORNING to the beacons for its own sends. Her Overview can therefore say
// "3 Sent" on one strip and "3 sent, 0 reached the device" on another, **and no instrument on this
// site says those are the same three sends and the morning was a failure.**
//
// This module is the join, and it imports nothing from `server/` so the SERVER composes with it and
// the DASHBOARD renders from it — `src/deliveryVerdict.js`'s shape, one question up.
//
// **THE FOUR THINGS IT REFUSES TO DO.**
//   1. **IT NEVER LETS A 201 WEAR THE WORD.** `delivered` is DERIVED from a displayed beacon through
//      `chainRow` and is reachable no other way. The ban is arithmetic, not a sentence in a comment.
//   2. **IT NEVER CALLS AN EMPTY MORNING GREEN.** A roster of nobody satisfies "every member's chain
//      is green" perfectly (R43's M8 / R129's empty set), and this is the one instrument where that
//      passes for a push system that is entirely dead. An empty audit is `quiet` and the gate is SHUT.
//   3. **IT NEVER FOLDS "NO BEACON AT ALL" INTO "THE DEVICE SAID NOTHING".** They are different facts
//      (R18) and the difference is the instrument's own health: a send the ledger recorded with NO
//      beacon row means `recordSent` did not run, or ran under a tag the audit cannot reproduce — and
//      a broken tag crossing puts EVERY member in that state at once. Folded into `silent` it reads
//      as the whole roster's phones failing; named, it reads as the one thing it is.
//   4. **IT NEVER ASSUMES AN UNKNOWN IS HARMLESS.** An unnamed cause is refused exactly as a failure
//      is (R141's `spends: "unknown"` ruling read straight: refuse, never assume).

import { chainRow, DELIVERY_CAUSES } from "./deliveryVerdict.js";

// ---------------------------------------------------------------------------
// THE STATES. **SEVEN, BECAUSE SEVEN THINGS REALLY HAPPEN TO A MEMBER'S MORNING** — and the roster is
// declared rather than left as whatever the reducer happens to emit, so the strip, the gate and the
// fixture are all naming one set (R17/R28/R29).
//
// `counts` is TRUE where the member's morning is a delivery question at all. A member the job
// correctly declined to send to is not a hole in the chain, and counting her as one is how a gate
// that should be green never opens.
// ---------------------------------------------------------------------------
export const MORNING_AUDIT_STATES = [
  {
    key: "delivered",
    label: "Delivered",
    counts: true,
    // THE ONLY STATE PERMITTED TO WEAR THE WORD, and it is reachable only through a displayed beacon.
    why: "The device reported it arrived and the notification drew. This is the only state on this roster that a beacon proved.",
  },
  {
    key: "drew-nothing",
    label: "Reached her, never drew",
    counts: true,
    why: "The worker reported it arrived and the notification never drew. The app on the device, not the endpoint.",
  },
  {
    key: "silent",
    label: "Never arrived",
    counts: true,
    why: "Sent, past the grace, and the device has said nothing at all.",
  },
  {
    key: "waiting",
    label: "Still in grace",
    counts: true,
    // DT-4a's fourth state, and the one a three-state version gets wrong: a phone in a lift is not a
    // defect. It does not FAIL the gate and it does not pass it either — it is not yet an answer.
    why: "Sent recently. A phone that is off or out of signal has not failed yet.",
  },
  {
    key: "unrecorded",
    label: "Sent with no beacon row",
    counts: true,
    // **THE STATE THAT INDICTS THE INSTRUMENT RATHER THAN THE MEMBER**, and the reason it is not
    // folded into `silent`. The ledger says the push service took it and `push_beacons` holds
    // nothing under its tag, so there is no evidence to be silent WITH.
    why: "The ledger recorded a send and no beacon row exists for its tag. Either the chain was never opened for it, or the tag the audit reproduces is not the tag it was sent under.",
  },
  {
    key: "skipped",
    label: "Not sent",
    counts: false,
    // DL-1c's division decides whether this blocks, and it is asked of the row rather than re-derived
    // here — see `auditRow`.
    why: "The job decided not to send. Whether that is a failure is DL-1c's question, and the row carries its answer.",
  },
  {
    key: "unfinished",
    label: "Started, never finished",
    counts: false,
    // DL-3.3's own state. Nobody decided anything, and because the claim is what makes a send single
    // this member gets nothing until the day rolls.
    why: "The row is still claimed. The pass died between claiming her morning and settling it.",
  },
];

export const MORNING_AUDIT_STATE_KEYS = MORNING_AUDIT_STATES.map((s) => s.key);
export const morningAuditState = (key) =>
  MORNING_AUDIT_STATES.find((s) => s.key === String(key || "")) || null;

// **THE WORD, DECLARED ONCE AND USED AS A PREDICATE.** Her ban is on a CLAIM, so it is expressed as
// the one question a claim has to answer rather than as a list of forbidden spellings: a state may
// be called delivered exactly when a beacon says the notification drew.
export const DELIVERED_STATE = "delivered";
export const isDeliveredState = (key) => String(key || "") === DELIVERED_STATE;

// **WHICH FAILURES HEAL THEMSELVES, DERIVED FROM DT-1c's ROSTER RATHER THAN LISTED AGAIN** (R31: the
// crossing stops existing rather than being watched). Her gate's own words are "green — sent =
// received = displayed, **or named-and-healing**", and `DELIVERY_CAUSES` already carries `act` for
// exactly this reason. A cause whose act says it heals itself is named-and-healing; everything else,
// `unknown` included, is ours to fix.
// **THE ROSTER IS A PARAMETER, AND THAT IS THIS ROUND'S OWN MUTATION RUN TALKING** (R129/R135/R145).
// Both guards below are behavioural no-ops against the roster DT-1c ships THIS MORNING — `unknown`'s
// act happens not to begin with the word, and an unrostered key resolves through `deliveryCause` to
// that same row — so a mutation deleting either stayed GREEN, correctly, and reported a hole that was
// really a bad mutation (R63). **A law's job is to be right about the roster, not about today's copy
// of it**, so the fixture drives this over a fabricated `DELIVERY_CAUSES` in which `unknown` DOES say
// it heals, which is the one input each clause can refuse (R28).
//
// It resolves INSIDE the given roster rather than through `deliveryCause`, whose documented fallback
// is the LAST entry — so an unrostered key would otherwise inherit whatever `unknown` says about
// itself, which is precisely the confident wrong answer DT-1c's roster exists to refuse.
export function causeHeals(cause, causes = DELIVERY_CAUSES) {
  const key = String(cause || "");
  const row = (Array.isArray(causes) ? causes : []).find((c) => c && c.key === key);
  if (!row || key === "unknown") return false;
  return /^Heals itself/.test(row.act || "");
}

// ---------------------------------------------------------------------------
// ONE MEMBER'S MORNING, RECONCILED.
//
// **THE LEDGER DECIDES WHETHER THERE WAS A SEND; THE BEACON DECIDES WHAT BECAME OF IT.** Asking them
// in the other order is how a skipped member ends up in a delivery gap list, and how a send with no
// beacon ends up wearing the verdict of a send that has one.
//
// `now` is a PARAMETER (R46/R142), so every state below is reachable in a fixture with no clock to
// wait on and no morning having had to go wrong first.
// ---------------------------------------------------------------------------
export function auditRow(row, { graceMinutes = 60, now = 0 } = {}) {
  const r = row && typeof row === "object" ? row : {};
  const status = String(r.status || "").trim();
  const who = String(r.email || "").trim() || (r.userId ? `member ${r.userId}` : "unknown member");
  const base = {
    userId: r.userId === null || r.userId === undefined ? null : String(r.userId),
    who,
    tag: String(r.tag || ""),
    reason: String(r.reason || ""),
    cause: "",
    minutes: null,
  };

  // A row nobody settled. Named before anything else, because its status is not a decision and every
  // question below assumes one was made.
  if (status === "claimed") return { ...base, state: "unfinished", heals: false };

  if (status !== "sent") {
    // **DL-1c's DIVISION IS ASKED OF THE ROW, NOT RE-DERIVED HERE.** `isDiagnosticReason` lives in
    // `server/daily-job.js` beside the roster it divides, and this module may not import `server/`
    // (the dashboard bundles it). So the STORE attaches the answer and this law consumes it — the
    // same split `push-beacons.js` already makes for `undeliveredCause`, and its own header states
    // the principle: the store gathers the evidence, it does not decide what the evidence means.
    // **A ROW THAT DID NOT SAY IS TREATED AS DIAGNOSTIC**, which is DL-1c's own direction: a skip
    // nothing classified is more likely to be a new failure mode than a new way of deciding not to
    // send, and this is the gate for opening the site to everybody.
    const diagnostic = r.diagnostic === undefined || r.diagnostic === null ? true : Boolean(r.diagnostic);
    return { ...base, state: "skipped", diagnostic, heals: false };
  }

  // **SENT, SO THE BEACON IS NOW THE ONLY THING ALLOWED TO SPEAK.** No beacon row at all is its own
  // answer and never `silent`: silence is a device declining to answer, and there is nothing here for
  // it to have declined.
  if (!r.tag || !r.hasBeacon) {
    return { ...base, state: "unrecorded", heals: false };
  }

  // **DERIVED THROUGH DT-4a's OWN READER (R31).** The three stamps decide delivered / drew-nothing /
  // silent / waiting exactly once in this repo, and a second copy of that arithmetic here is how the
  // member modal and the morning audit come to disagree about one send.
  const chain = chainRow(
    { tag: r.tag, kind: r.kind, device: r.device, sent_at: r.sentAt, received_at: r.receivedAt, displayed_at: r.displayedAt },
    { graceMinutes, now },
  );
  const cause = chain.state === "silent" ? String(r.cause || "unknown") : "";
  return {
    ...base,
    state: chain.state,
    minutes: chain.minutes,
    cause,
    // Only a real silence can be healing: a push that reached the worker and never drew is the app on
    // the device, and nothing about re-minting an endpoint touches it.
    heals: chain.state === "silent" && causeHeals(cause),
  };
}

// ---------------------------------------------------------------------------
// THE MORNING, WHOLE. **THE ROWS ARE THE DELIVERABLE AND THE COUNTS RIDE BESIDE THEM** — her order
// says "per member, the gap list", and a percentage is not something any member experiences.
// ---------------------------------------------------------------------------
export function morningAudit(rows, { dayKey = "", graceMinutes = 60, now = 0 } = {}) {
  const list = (Array.isArray(rows) ? rows : []).map((r) => auditRow(r, { graceMinutes, now }));
  const count = (k) => list.filter((r) => r.state === k).length;
  const counts = {};
  for (const key of MORNING_AUDIT_STATE_KEYS) counts[key] = count(key);

  // **THE GAP LIST IS THE STATES A CHAIN CAN BE BROKEN IN, PLUS THE SKIPS THAT WERE FAILURES.** A
  // routine skip is not a gap; a `line-refused` is a morning that went wrong and is exactly what her
  // strip must not lose.
  const gaps = list.filter(
    (r) => r.state === "drew-nothing" || r.state === "silent" || r.state === "unrecorded"
      || r.state === "unfinished" || (r.state === "skipped" && r.diagnostic),
  );

  // Ranked by how bad, then by NAME, so two equal rows do not swap places between refreshes and read
  // as movement (DL-3.3's tiebreak, and the same reason).
  const rank = { unrecorded: 0, silent: 1, "drew-nothing": 2, unfinished: 3, skipped: 4 };
  gaps.sort((a, b) => (rank[a.state] ?? 9) - (rank[b.state] ?? 9) || a.who.localeCompare(b.who));

  const sent = counts.delivered + counts["drew-nothing"] + counts.silent + counts.waiting + counts.unrecorded;
  // **THREE STATES, AND `quiet` IS THE LOAD-BEARING ONE** (DT-1c's ruling, one instrument over). A
  // morning nobody was sent to is not a morning that went well.
  const state = list.length === 0 ? "quiet" : gaps.length ? "broken" : counts.waiting ? "waiting" : "clean";
  return {
    day: String(dayKey || ""),
    graceMinutes: Math.max(1, Number(graceMinutes) || 60),
    members: list.length,
    sent,
    delivered: counts.delivered,
    counts,
    gaps,
    rows: list,
    state,
  };
}

export const MORNING_AUDIT_QUIET_NOTE =
  "No mornings were recorded for this day, so there is nothing here to be clean.";
export const MORNING_AUDIT_BROKEN_NOTE =
  "These mornings have no beacon saying they arrived. Until one comes back they were not delivered, whatever the push service returned.";

export function morningAuditNote(audit) {
  if (!audit || typeof audit !== "object") return "";
  if (audit.state === "quiet") return MORNING_AUDIT_QUIET_NOTE;
  if (audit.state === "broken") return MORNING_AUDIT_BROKEN_NOTE;
  return "";
}

// ---------------------------------------------------------------------------
// DT-5 — **THE GATE FOR THE ELITES, AND IT IS A FUNCTION RATHER THAN A JUDGEMENT SOMEBODY MAKES ON
// THE MORNING.**
//
// Her order: *"The all-members morning open proceeds ONLY when a full morning run shows the chain
// green — sent = received = displayed, or named-and-healing — for every rostered member. If the chain
// shows gaps, the gaps are the work and the open waits for a clean chain rather than for a lucky
// morning."*
//
// **IT IS WRITTEN TO SHUT, AND EVERY REASON IT SHUTS FOR IS NAMED.** A gate whose default is open is
// a gate that opens on the morning its own evidence failed to gather — which is the single most
// likely way this one is ever wrong, because a broken tag crossing produces an empty or unrecorded
// audit rather than a red one.
// ---------------------------------------------------------------------------
export const MORNING_GATE_SHUT_QUIET = "No morning has run under the beacon yet. The chain cannot be green before there are rows in it.";
export const MORNING_GATE_SHUT_GAPS = "The chain has gaps. The gaps are the work.";
export const MORNING_GATE_SHUT_WAITING = "Sends are still inside the grace. The chain has not answered yet.";
export const MORNING_GATE_OPEN = "Every rostered member's chain is green or named-and-healing.";

export function morningGate(audit) {
  const a = audit && typeof audit === "object" ? audit : null;
  const rows = Array.isArray(a && a.rows) ? a.rows : [];
  // **AN EMPTY ROSTER SATISFIES "EVERY MEMBER" PERFECTLY**, which is why this is the first clause and
  // not a footnote: without it the gate is green the morning the job did not run at all.
  if (!rows.length) return { open: false, reason: MORNING_GATE_SHUT_QUIET, blocking: [] };

  const blocking = rows.filter((r) => {
    if (r.state === "delivered") return false;
    // A routine skip is not a hole in the chain — she was correctly not sent to.
    if (r.state === "skipped") return Boolean(r.diagnostic);
    // Named-and-healing, her own words, and it is the ONLY concession this gate makes.
    if (r.state === "silent" && r.heals) return false;
    return true;
  });
  if (blocking.length) {
    const waitingOnly = blocking.every((r) => r.state === "waiting");
    return {
      open: false,
      reason: waitingOnly ? MORNING_GATE_SHUT_WAITING : MORNING_GATE_SHUT_GAPS,
      blocking,
    };
  }
  return { open: true, reason: MORNING_GATE_OPEN, blocking: [] };
}
